import { Color3, DynamicTexture, type Mesh, StandardMaterial } from "@babylonjs/core";
import { HERO_LIST, type HeroId, Role } from "@rivalwatch/shared";
import { renderHeroPortraits } from "../../game/portraits.ts";
import { fit, inkBust, mix, roundRect } from "../paint.ts";
import type { DressContext, Owned } from "../types.ts";

/** What tools/map/orlando.py writes on each MURAL_* / SIGN_* panel (glTF node extras). */
interface PanelExtras {
  w?: number;
  h?: number;
  style?: "postcard" | "roadside" | "crate" | "neon" | "arrow" | "tin" | "street" | "diamond" | "carved";
  /** Painted upside down (the topsy-turvy house's). */
  flip?: number;
  text?: string;
  /** Neon: the tube's colour. */
  color?: string;
  role?: "vanguard" | "duelist" | "strategist";
  /** The hero a billboard features (a roster slug); any hero of `role` if it's not in the roster. */
  hero?: string;
  ground?: string;
  title?: string;
  greeting?: string;
  caption?: string;
  balloon?: string;
}

const INK = "#1f2226";
const CREAM = "#f6eed8";
const ROLES: Record<string, Role> = { vanguard: Role.Tank, duelist: Role.Damage, strategist: Role.Support };
const FONTS = ["400 64px Pacifico", "900 64px 'Big Shoulders Display'", "700 64px 'Big Shoulders Display'", "700 64px 'Comic Neue'"];
const SCRIPT = (px: number) => `400 ${px}px Pacifico, 'Brush Script MT', cursive`;
const BLOCK = (px: number) => `900 ${px}px 'Big Shoulders Display', Impact, 'Arial Narrow', sans-serif`;
const BOLD = (px: number) => `700 ${px}px 'Big Shoulders Display', 'Arial Narrow', sans-serif`;
const COMIC = (px: number) => `700 ${px}px 'Comic Neue', 'Comic Sans MS', sans-serif`;

type G = CanvasRenderingContext2D;
type Bust = HTMLCanvasElement | undefined;

/** Pixels per metre: billboards are read from across a stage, signs up close. */
const density = (mural: boolean, style?: string) => (mural ? 96 : style === "street" ? 260 : 160);

/**
 * Paints Orlando's panels in the Florida tourist trade's hand: a large-letter linen postcard at
 * Lake Eola, a roadside attraction billboard on International Drive and a citrus crate label on
 * the Orange Blossom Trail, each with a hero rendered from their real model and inked; and the
 * signs: neon (lit, it blooms), painted tin, a wooden arrow and green street blades.
 */
export function paintPanels(ctx: DressContext): Owned[] {
  const out: Owned[] = [];
  let disposed = false;
  const jobs: (() => void)[] = [];
  const murals: HeroId[] = [];
  const portraits: Partial<Record<HeroId, HTMLCanvasElement>> = {};

  for (const mesh of ctx.panels) {
    const ex = ((mesh.metadata as { gltf?: { extras?: PanelExtras } } | null)?.gltf?.extras ?? {}) as PanelExtras;
    const mural = mesh.name.startsWith("MURAL_");
    const d = density(mural, ex.style);
    const W = Math.min(2048, Math.max(64, Math.round((ex.w ?? 1) * d)));
    const H = Math.min(2048, Math.max(64, Math.round((ex.h ?? 1) * d)));
    const tex = new DynamicTexture(`panel-${mesh.name}`, { width: W, height: H }, ctx.scene, true);
    tex.wrapU = tex.wrapV = DynamicTexture.CLAMP_ADDRESSMODE;
    const mat = new StandardMaterial(`panel-${mesh.name}`, ctx.scene);
    mat.diffuseTexture = tex;
    mat.specularColor = Color3.Black();
    mat.zOffset = -1;
    if (ex.style === "neon") {
      // Lit tubes: unlit, and bright enough for the glow layer to bloom them.
      mat.emissiveTexture = tex;
      mat.disableLighting = true;
    }
    if (ex.style === "arrow" || ex.style === "diamond") {
      tex.hasAlpha = true;
      mat.useAlphaFromDiffuseTexture = true;
      mat.transparencyMode = StandardMaterial.MATERIAL_ALPHATEST;
    }
    (mesh as Mesh).material = mat;
    mesh.receiveShadows = true;
    out.push(tex, mat);
    const g = tex.getContext() as unknown as G;
    let hero: HeroId | undefined;
    if (mural) {
      const heroes = HERO_LIST.filter((hd) => hd.role === ROLES[ex.role ?? "duelist"]);
      let hash = 0;
      for (const ch of mesh.name) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
      hero = (HERO_LIST.find((hd) => hd.slug === ex.hero) ?? heroes[hash % heroes.length]!).id;
      murals.push(hero);
    }
    jobs.push(() => {
      const bust = hero !== undefined ? portraits[hero] : undefined;
      g.save();
      if (ex.flip) {
        g.translate(W, H);
        g.rotate(Math.PI);
      }
      if (ex.style === "postcard") paintPostcard(g, W, H, ex, hero!, bust);
      else if (ex.style === "roadside") paintRoadside(g, W, H, ex, hero!, bust);
      else if (ex.style === "crate") paintCrate(g, W, H, ex, hero!, bust);
      else if (ex.style === "neon") paintNeon(g, W, H, ex.text ?? "", ex.color ?? "#ffe377");
      else if (ex.style === "arrow") paintArrow(g, W, H, ex.text ?? "");
      else if (ex.style === "diamond") paintDiamond(g, W, H, ex.text ?? "");
      else if (ex.style === "carved") paintCarved(g, W, H, ex.text ?? "");
      else if (ex.style === "tin") paintTin(g, W, H, ex.text ?? "");
      else paintStreet(g, W, H, ex.text ?? "");
      g.restore();
      tex.update(false);
    });
  }
  out.push({ dispose: () => void (disposed = true) });
  const paintAll = () => {
    if (!disposed) for (const job of jobs) job();
  };
  paintAll();
  Promise.all(FONTS.map((f) => document.fonts.load(f)))
    .then(paintAll)
    .catch(() => undefined);
  if (murals.length) {
    ctx.scene.onAfterRenderObservable.addOnce(() => {
      const ids = [...new Set(murals)];
      renderHeroPortraits(ctx.engine, 512, ids)
        .then(async (urls) => {
          for (const id of ids) {
            const url = urls[id];
            if (!url) continue;
            const img = new Image();
            img.src = url;
            await img.decode();
            portraits[id] = inkBust(img, INK);
          }
          paintAll();
        })
        .catch((e) => console.warn("billboard portraits unavailable", e));
    });
  }
  return out;
}

// ---------------------------------------------------------------- shared strokes
function sunburst(g: G, cx: number, cy: number, r: number, colour: string, rays = 20, phase = 0) {
  g.fillStyle = colour;
  for (let i = 0; i < rays; i += 2) {
    const a0 = phase + (i / rays) * Math.PI * 2;
    const a1 = phase + ((i + 1) / rays) * Math.PI * 2;
    g.beginPath();
    g.moveTo(cx, cy);
    g.lineTo(cx + Math.cos(a0) * r, cy + Math.sin(a0) * r);
    g.lineTo(cx + Math.cos(a1) * r, cy + Math.sin(a1) * r);
    g.closePath();
    g.fill();
  }
}

/** Big block lettering, each letter its own colour, with a white inline, an ink outline and a
 * dark extrusion down and to the right: the large-letter postcard's alphabet. */
function blockLetters(g: G, text: string, x: number, y: number, maxW: number, maxH: number, fills: string[], depth: string) {
  const px = fit(g, text, BLOCK, maxW, maxH);
  g.font = BLOCK(px);
  g.textBaseline = "alphabetic";
  g.textAlign = "left";
  const widths = [...text].map((ch) => g.measureText(ch).width);
  const total = widths.reduce((a, b) => a + b, 0);
  let cx = x - total / 2;
  const ext = px * 0.07;
  g.lineJoin = "round";
  [...text].forEach((ch, i) => {
    for (let k = ext; k > 0; k -= Math.max(1, ext / 6)) {
      g.fillStyle = depth;
      g.fillText(ch, cx + k, y + k);
    }
    g.lineWidth = px * 0.075;
    g.strokeStyle = INK;
    g.strokeText(ch, cx + ext, y + ext);
    g.strokeText(ch, cx, y);
    g.fillStyle = fills[i % fills.length]!;
    g.fillText(ch, cx, y);
    g.lineWidth = px * 0.018;
    g.strokeStyle = "#ffffff";
    g.save();
    g.beginPath();
    g.rect(cx, y - px, widths[i]!, px);
    g.clip();
    g.strokeText(ch, cx - px * 0.012, y - px * 0.012);
    g.restore();
    cx += widths[i]!;
  });
  return px;
}

/** The hero's bust, breaking out over the bottom edge of the picture (or its silhouette until
 * the render arrives). */
function drawBust(g: G, bust: Bust, cx: number, bottom: number, s: number, shade: string) {
  const bx = cx - s * 0.5;
  const by = bottom - s * 0.84;
  if (bust) g.drawImage(bust, bx, by, s, s);
  else {
    g.fillStyle = shade;
    g.beginPath();
    g.ellipse(cx, by + s * 0.36, s * 0.16, s * 0.19, 0, 0, Math.PI * 2);
    g.fill();
    g.beginPath();
    g.ellipse(cx, by + s * 0.86, s * 0.42, s * 0.3, 0, Math.PI, 0);
    g.fill();
  }
  return { bx, by };
}

function balloon(g: G, cx: number, cy: number, bw: number, bh: number, tail: [number, number], text: string, lw: number) {
  const [a = "", b = ""] = text.split("|");
  g.fillStyle = "#ffffff";
  g.strokeStyle = INK;
  g.lineWidth = lw;
  g.beginPath();
  g.moveTo(cx - bw * 0.1, cy + bh * 0.38);
  g.lineTo(tail[0], tail[1]);
  g.lineTo(cx + bw * 0.1, cy + bh * 0.4);
  g.closePath();
  g.fill();
  g.stroke();
  g.beginPath();
  g.ellipse(cx, cy, bw / 2, bh / 2, 0, 0, Math.PI * 2);
  g.fill();
  g.stroke();
  g.beginPath();
  g.ellipse(cx, cy, bw / 2 - lw * 0.6, bh / 2 - lw * 0.6, 0, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = INK;
  g.textAlign = "center";
  g.textBaseline = "middle";
  fit(g, a, COMIC, bw * 0.78, bh * 0.36);
  g.fillText(a, cx, cy - bh * 0.12);
  fit(g, b, COMIC, bw * 0.72, bh * 0.24);
  g.fillText(b, cx, cy + bh * 0.2);
}

function palmSilhouette(g: G, x: number, base: number, h: number, lean: number, colour: string) {
  g.strokeStyle = colour;
  g.lineCap = "round";
  g.lineWidth = h * 0.05;
  const tx = x + lean;
  const ty = base - h;
  g.beginPath();
  g.moveTo(x, base);
  g.quadraticCurveTo(x + lean * 0.2, base - h * 0.6, tx, ty);
  g.stroke();
  g.fillStyle = colour;
  for (let k = 0; k < 7; k++) {
    const a = -Math.PI / 2 + ((k - 3) / 3) * 1.45;
    const L = h * (0.42 + 0.08 * Math.cos(k));
    const ex = tx + Math.cos(a) * L;
    const ey = ty + Math.sin(a) * L * 0.55 + L * 0.35;
    g.beginPath();
    g.moveTo(tx, ty);
    g.quadraticCurveTo(tx + Math.cos(a) * L * 0.6, ty + Math.sin(a) * L * 0.6 - h * 0.08, ex, ey);
    g.quadraticCurveTo(tx + Math.cos(a) * L * 0.5, ty + Math.sin(a) * L * 0.5, tx, ty + h * 0.03);
    g.fill();
  }
}

// ---------------------------------------------------------------- Lake Eola: a linen postcard
/**
 * A large-letter postcard: "Greetings from" in script over ORLANDO in block letters, each its own
 * colour, above the lake with its fountain and palms; the hero stepping out of the picture over
 * the caption; a stamp in the corner. English over Spanish, as Orlando signs itself.
 */
function paintPostcard(g: G, W: number, H: number, ex: PanelExtras, hero: HeroId, bust: Bust) {
  const lw = W * 0.012;
  g.fillStyle = CREAM;
  g.fillRect(0, 0, W, H);
  const m = W * 0.045;
  const x0 = m;
  const y0 = m;
  const x1 = W - m;
  const y1 = H - m * 3.1;
  g.save();
  g.beginPath();
  g.rect(x0, y0, x1 - x0, y1 - y0);
  g.clip();
  const sky = ex.ground ?? "#f3b79a";
  g.fillStyle = "#a9dbe6";
  g.fillRect(x0, y0, x1 - x0, y1 - y0);
  g.fillStyle = sky;
  g.fillRect(x0, y0 + (y1 - y0) * 0.5, x1 - x0, (y1 - y0) * 0.5);
  sunburst(g, W / 2, y0 + (y1 - y0) * 0.64, H, mix(sky, "#ffffff", 0.35), 22, 0.07);
  // The lake, its fountain and the palms on the shore.
  const lake = y0 + (y1 - y0) * 0.8;
  g.fillStyle = "#5e9e96";
  g.fillRect(x0, lake, x1 - x0, y1 - lake);
  g.fillStyle = "#8dc8b8";
  for (let k = 0; k < 5; k++) g.fillRect(x0, lake + (y1 - lake) * (0.2 + 0.17 * k), x1 - x0, H * 0.004);
  const fx = x0 + (x1 - x0) * 0.17;
  g.fillStyle = "#f4f6f0";
  g.beginPath();
  g.moveTo(fx - W * 0.012, lake - H * 0.03);
  g.lineTo(fx, lake - H * 0.2);
  g.lineTo(fx + W * 0.012, lake - H * 0.03);
  g.fill();
  g.fillStyle = "#5fa597";
  g.beginPath();
  g.ellipse(fx, lake, W * 0.075, H * 0.045, 0, Math.PI, 0);
  g.fill();
  g.strokeStyle = INK;
  g.lineWidth = lw * 0.5;
  g.stroke();
  palmSilhouette(g, x1 - W * 0.07, lake + H * 0.01, H * 0.34, -W * 0.05, "#2f5d3a");
  palmSilhouette(g, x1 - W * 0.16, lake + H * 0.01, H * 0.26, W * 0.03, "#3c6e45");
  palmSilhouette(g, x0 + W * 0.33, lake + H * 0.01, H * 0.2, W * 0.02, "#3c6e45");
  // Greetings from ORLANDO.
  const [greet = "Greetings from", saludo = ""] = (ex.greeting ?? "Greetings from").split("|");
  g.save();
  g.translate(x0 + (x1 - x0) * 0.08, y0 + (y1 - y0) * 0.14);
  g.rotate(-0.06);
  g.textAlign = "left";
  g.textBaseline = "alphabetic";
  const sp = fit(g, greet, SCRIPT, (x1 - x0) * 0.62, H * 0.085);
  g.font = SCRIPT(sp);
  g.lineJoin = "round";
  g.lineWidth = sp * 0.14;
  g.strokeStyle = INK;
  g.strokeText(greet, 0, 0);
  g.fillStyle = "#ffffff";
  g.fillText(greet, 0, 0);
  if (saludo) {
    g.font = SCRIPT(sp * 0.5);
    g.lineWidth = sp * 0.08;
    g.strokeText(saludo, sp * 0.4, sp * 0.62);
    g.fillStyle = "#fff4d6";
    g.fillText(saludo, sp * 0.4, sp * 0.62);
  }
  g.restore();
  blockLetters(g, ex.title ?? "ORLANDO", W / 2, y0 + (y1 - y0) * 0.43, (x1 - x0) * 0.94, H * 0.22, ["#f29b30", "#5fa597", "#eab0b5", "#f2cc4e", "#86b85a", "#f29b30", "#5fa597"], "#7a4a2a");
  g.restore();
  g.lineWidth = lw * 1.2;
  g.strokeStyle = INK;
  g.strokeRect(x0, y0, x1 - x0, y1 - y0);
  // The stamp: a perforated square with an orange on it.
  const ss = W * 0.12;
  const sx = x1 - ss - m * 0.4;
  const sy = y0 + m * 0.4;
  g.fillStyle = CREAM;
  g.beginPath();
  const teeth = 10;
  for (let k = 0; k < teeth * 4; k++) {
    const side = Math.floor(k / teeth);
    const f = (k % teeth) / teeth;
    const pts: [number, number][] = [
      [sx + ss * f, sy],
      [sx + ss, sy + ss * f],
      [sx + ss * (1 - f), sy + ss],
      [sx, sy + ss * (1 - f)],
    ];
    const [px, py] = pts[side]!;
    if (k === 0) g.moveTo(px, py);
    else g.lineTo(px, py);
  }
  g.closePath();
  g.fill();
  g.lineWidth = lw * 0.5;
  g.stroke();
  g.fillStyle = "#86bcb0";
  g.fillRect(sx + ss * 0.12, sy + ss * 0.12, ss * 0.76, ss * 0.76);
  g.fillStyle = "#f29b30";
  g.beginPath();
  g.arc(sx + ss / 2, sy + ss * 0.55, ss * 0.22, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = "#3f7d3a";
  g.beginPath();
  g.ellipse(sx + ss * 0.6, sy + ss * 0.3, ss * 0.12, ss * 0.05, -0.5, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = INK;
  g.font = BOLD(ss * 0.14);
  g.textAlign = "center";
  g.fillText("FLA · 4¢", sx + ss / 2, sy + ss * 0.94);
  // The hero, stepping out over the caption.
  drawBust(g, bust, W * 0.5, y1 + m * 1.2, W * 0.66, mix(sky, INK, 0.5));
  // The caption band.
  const [cap = "", cap2 = ""] = (ex.caption ?? "").split("|");
  g.fillStyle = INK;
  g.textAlign = "left";
  g.textBaseline = "middle";
  fit(g, cap, BOLD, (x1 - x0) * 0.3, m * 1.1);
  g.fillText(cap, x0, y1 + m * 1.05);
  g.fillStyle = mix(CREAM, INK, 0.55);
  fit(g, cap2, BOLD, (x1 - x0) * 0.3, m * 0.8);
  g.fillText(cap2, x0, y1 + m * 2.1);
  g.textAlign = "right";
  fit(g, HERO_LIST[hero]!.name.toUpperCase(), BOLD, (x1 - x0) * 0.26, m * 0.9);
  g.fillStyle = INK;
  g.fillText(HERO_LIST[hero]!.name.toUpperCase(), x1, y1 + m * 1.05);
  g.fillStyle = mix(CREAM, INK, 0.55);
  g.font = BOLD(m * 0.55);
  g.fillText("RIVALWATCH · ORL", x1, y1 + m * 2.1);
}

// ---------------------------------------------------------------- International Drive: a roadside billboard
/**
 * A roadside attraction billboard of the kind that lines I-4: a shouting headline, a sunburst,
 * the hero with a snow cone, a tagline ribbon and a big arrow to the next exit, English over
 * Spanish.
 */
function paintRoadside(g: G, W: number, H: number, ex: PanelExtras, hero: HeroId, bust: Bust) {
  const lw = H * 0.012;
  const ground = ex.ground ?? "#f08a6c";
  g.fillStyle = "#fbf4e2";
  g.fillRect(0, 0, W, H);
  const m = H * 0.045;
  g.save();
  g.beginPath();
  g.rect(m, m, W - 2 * m, H - 2 * m);
  g.clip();
  g.fillStyle = ground;
  g.fillRect(0, 0, W, H);
  sunburst(g, W * 0.76, H * 0.62, W, mix(ground, "#fff2c8", 0.4), 26, 0.1);
  // The headline on a white splash.
  const cx = W * 0.34;
  g.fillStyle = "#ffffff";
  g.beginPath();
  for (let k = 0; k < 28; k++) {
    const a = (k / 28) * Math.PI * 2;
    const r = (k % 2 ? 0.86 : 1.0) * W * 0.3;
    g.lineTo(cx + Math.cos(a) * r, H * 0.36 + Math.sin(a) * r * 0.52);
  }
  g.closePath();
  g.fill();
  g.strokeStyle = INK;
  g.lineWidth = lw;
  g.stroke();
  const words = (ex.title ?? "BEAT THE HEAT!").split(" ");
  const top = words.slice(0, Math.ceil(words.length / 2)).join(" ");
  const bottom = words.slice(Math.ceil(words.length / 2)).join(" ");
  blockLetters(g, top, cx, H * 0.33, W * 0.46, H * 0.25, ["#f2cc4e"], "#a94f33");
  blockLetters(g, bottom, cx, H * 0.58, W * 0.5, H * 0.27, ["#6ff2d8", "#f2cc4e", "#eab0b5"], "#a94f33");
  // A snow cone beside it.
  const sx = W * 0.62;
  const sy = H * 0.52;
  const r = H * 0.11;
  g.fillStyle = "#f6f1e1";
  g.beginPath();
  g.moveTo(sx - r, sy);
  g.lineTo(sx, sy + r * 2.4);
  g.lineTo(sx + r, sy);
  g.closePath();
  g.fill();
  g.lineWidth = lw * 0.7;
  g.stroke();
  for (const [dx, col] of [[-0.45, "#6ff2d8"], [0.0, "#eab0b5"], [0.45, "#f2cc4e"]] as const) {
    g.fillStyle = col;
    g.beginPath();
    g.arc(sx + dx * r * 1.1, sy - r * 0.15, r * 0.62, 0, Math.PI * 2);
    g.fill();
    g.stroke();
  }
  // The arrow to the exit.
  const [tag = "", tag2 = ""] = (ex.caption ?? "").split("|");
  const ay = H * 0.8;
  const ah = H * 0.16;
  const ax0 = m * 2;
  const ax1 = W * 0.6;
  g.fillStyle = "#2f6b58";
  g.beginPath();
  g.moveTo(ax0, ay - ah / 2);
  g.lineTo(ax1 - ah * 0.7, ay - ah / 2);
  g.lineTo(ax1 - ah * 0.7, ay - ah * 0.85);
  g.lineTo(ax1, ay);
  g.lineTo(ax1 - ah * 0.7, ay + ah * 0.85);
  g.lineTo(ax1 - ah * 0.7, ay + ah / 2);
  g.lineTo(ax0, ay + ah / 2);
  g.closePath();
  g.fill();
  g.lineWidth = lw;
  g.strokeStyle = INK;
  g.stroke();
  g.fillStyle = "#ffffff";
  g.textAlign = "left";
  g.textBaseline = "middle";
  const [first = "", second = ""] = tag2.split(" · ");
  fit(g, first, BLOCK, (ax1 - ax0) * 0.62, ah * 0.5);
  g.fillText(first, ax0 + ah * 0.25, ay - ah * 0.14);
  fit(g, second, BOLD, (ax1 - ax0) * 0.62, ah * 0.3);
  g.fillText(second, ax0 + ah * 0.25, ay + ah * 0.26);
  // The tagline, in script on a ribbon.
  const ry = H * 0.12;
  g.fillStyle = "#2f6b58";
  g.beginPath();
  g.moveTo(W * 0.08, ry - H * 0.055);
  g.lineTo(W * 0.56, ry - H * 0.055);
  g.lineTo(W * 0.53, ry);
  g.lineTo(W * 0.56, ry + H * 0.055);
  g.lineTo(W * 0.08, ry + H * 0.055);
  g.lineTo(W * 0.11, ry);
  g.closePath();
  g.fill();
  g.lineWidth = lw * 0.8;
  g.stroke();
  g.fillStyle = "#ffffff";
  g.textAlign = "center";
  fit(g, tag, SCRIPT, W * 0.4, H * 0.085);
  g.fillText(tag, W * 0.32, ry + H * 0.005);
  // The hero, big, stepping off the board's foot.
  drawBust(g, bust, W * 0.8, H + m * 0.4, H * 1.02, mix(ground, INK, 0.5));
  g.restore();
  g.lineWidth = lw * 1.4;
  g.strokeStyle = INK;
  g.strokeRect(m, m, W - 2 * m, H - 2 * m);
  g.fillStyle = mix("#fbf4e2", INK, 0.55);
  g.font = BOLD(m * 0.7);
  g.textAlign = "right";
  g.textBaseline = "middle";
  g.fillText(`${HERO_LIST[hero]!.name.toUpperCase()} · RIVALWATCH OUTDOOR`, W - m, H - m * 0.5);
}

// ---------------------------------------------------------------- the Orange Blossom Trail: a crate label
/**
 * A citrus crate label, as the packing houses of the Trail pasted on their boxes: a sunrise over
 * the grove rows on a deep night-blue ground, a great orange, the brand on a gilt ribbon, the hero
 * and a speech balloon, and the grade and packer in the cream foot band.
 */
function paintCrate(g: G, W: number, H: number, ex: PanelExtras, hero: HeroId, bust: Bust) {
  const lw = H * 0.012;
  const gold = "#d9a93a";
  const navy = "#1f2d55";
  g.fillStyle = CREAM;
  g.fillRect(0, 0, W, H);
  const m = H * 0.05;
  g.fillStyle = gold;
  g.fillRect(m * 0.4, m * 0.4, W - m * 0.8, H - m * 0.8);
  g.fillStyle = CREAM;
  g.fillRect(m * 0.7, m * 0.7, W - m * 1.4, H - m * 1.4);
  const x0 = m;
  const y0 = m;
  const x1 = W - m;
  const y1 = H - m * 2.8;
  g.save();
  g.beginPath();
  g.rect(x0, y0, x1 - x0, y1 - y0);
  g.clip();
  g.fillStyle = navy;
  g.fillRect(x0, y0, x1 - x0, y1 - y0);
  // The rising sun and its rays over the grove.
  const hy = y0 + (y1 - y0) * 0.74;
  sunburst(g, x0 + (x1 - x0) * 0.55, hy, W, "#2e3f70", 30, 0.05);
  g.fillStyle = ex.ground ?? "#f29b30";
  g.beginPath();
  g.arc(x0 + (x1 - x0) * 0.55, hy, (y1 - y0) * 0.32, Math.PI, 0);
  g.fill();
  for (let r = 0; r < 4; r++) {
    const ry = hy + (y1 - hy) * (r / 4);
    g.fillStyle = r % 2 ? "#3f7d3a" : "#4e8a45";
    g.fillRect(x0, ry, x1 - x0, (y1 - hy) / 4 + 1);
    g.fillStyle = r % 2 ? "#2f6230" : "#386e36";
    for (let k = 0; k < 18; k++) {
      g.beginPath();
      g.arc(x0 + ((k + (r % 2) * 0.5) / 18) * (x1 - x0), ry + (y1 - hy) / 8, (y1 - hy) / 7, Math.PI, 0);
      g.fill();
    }
  }
  // The great orange with its leaves, on the left.
  const ox = x0 + (x1 - x0) * 0.16;
  const oy = y0 + (y1 - y0) * 0.54;
  const orr = (y1 - y0) * 0.26;
  g.fillStyle = "#3f7d3a";
  for (const a of [-0.9, -0.2]) {
    g.beginPath();
    g.ellipse(ox + Math.cos(a) * orr * 0.9, oy - orr * 0.95 + Math.sin(a) * orr * 0.2, orr * 0.45, orr * 0.18, a, 0, Math.PI * 2);
    g.fill();
    g.strokeStyle = INK;
    g.lineWidth = lw * 0.6;
    g.stroke();
  }
  g.fillStyle = "#f29b30";
  g.beginPath();
  g.arc(ox, oy, orr, 0, Math.PI * 2);
  g.fill();
  g.lineWidth = lw;
  g.stroke();
  g.fillStyle = "#f7c26a";
  g.beginPath();
  g.ellipse(ox - orr * 0.35, oy - orr * 0.35, orr * 0.28, orr * 0.16, -0.7, 0, Math.PI * 2);
  g.fill();
  // The brand on a gilt ribbon, and BRAND in script.
  const bx = x0 + (x1 - x0) * 0.47;
  const by = y0 + (y1 - y0) * 0.2;
  const rw = (x1 - x0) * 0.5;
  const rh = (y1 - y0) * 0.2;
  g.fillStyle = gold;
  g.beginPath();
  g.moveTo(bx - rw / 2 - rh * 0.4, by - rh / 2);
  g.lineTo(bx + rw / 2 + rh * 0.4, by - rh / 2);
  g.lineTo(bx + rw / 2, by);
  g.lineTo(bx + rw / 2 + rh * 0.4, by + rh / 2);
  g.lineTo(bx - rw / 2 - rh * 0.4, by + rh / 2);
  g.lineTo(bx - rw / 2, by);
  g.closePath();
  g.fill();
  g.strokeStyle = INK;
  g.lineWidth = lw;
  g.stroke();
  const [brand = "BRAND", grade = "", grade2 = ""] = (ex.caption ?? "BRAND").split("|").flatMap((part, i) => (i === 1 ? part.split(" · ") : [part]));
  g.fillStyle = navy;
  g.textAlign = "center";
  g.textBaseline = "middle";
  fit(g, ex.title ?? "", BLOCK, rw * 0.94, rh * 0.84);
  g.fillText(ex.title ?? "", bx, by + rh * 0.04);
  g.fillStyle = "#ffffff";
  g.lineWidth = lw * 0.9;
  g.strokeStyle = INK;
  g.lineJoin = "round";
  const sp = fit(g, brand, SCRIPT, rw * 0.4, rh * 0.75);
  g.font = SCRIPT(sp);
  g.strokeText(brand, bx, by + rh * 0.9);
  g.fillText(brand, bx, by + rh * 0.9);
  // The hero, and what they're shouting.
  drawBust(g, bust, x0 + (x1 - x0) * 0.8, y1 + m * 1.9, (y1 - y0) * 1.08, mix(navy, "#ffffff", 0.25));
  g.restore();
  if (ex.balloon) balloon(g, x0 + (x1 - x0) * 0.47, y0 + (y1 - y0) * 0.62, (x1 - x0) * 0.28, (y1 - y0) * 0.24, [x0 + (x1 - x0) * 0.66, y0 + (y1 - y0) * 0.45], ex.balloon, lw);
  g.lineWidth = lw * 1.2;
  g.strokeStyle = INK;
  g.strokeRect(x0, y0, x1 - x0, y1 - y0);
  // The foot band: grade, packer.
  g.fillStyle = navy;
  g.textAlign = "left";
  g.textBaseline = "middle";
  fit(g, grade, BLOCK, (x1 - x0) * 0.45, m * 1.3);
  g.fillText(grade, x0, y1 + m * 0.95);
  g.fillStyle = "#3f7d3a";
  fit(g, grade2, BOLD, (x1 - x0) * 0.2, m * 1.0);
  g.fillText(grade2, x0 + (x1 - x0) * 0.47, y1 + m * 0.95);
  g.fillStyle = mix(CREAM, INK, 0.5);
  g.textAlign = "right";
  g.font = BOLD(m * 0.62);
  g.fillText(`PACKED BY INDIAN RIVER CITRUS · ORLANDO, FLA. · ${HERO_LIST[hero]!.name.toUpperCase()}`, x1, y1 + m * 1.9);
}

// ---------------------------------------------------------------- signs
/** A neon sign: glowing tubes on a dark ground, a script or block word over a smaller line. */
function paintNeon(g: G, W: number, H: number, text: string, colour: string) {
  const [a = "", b = ""] = text.split("|");
  g.fillStyle = "#1c2024";
  g.fillRect(0, 0, W, H);
  g.strokeStyle = "#3b4248";
  g.lineWidth = H * 0.04;
  roundRect(g, H * 0.04, H * 0.04, W - H * 0.08, H - H * 0.08, H * 0.08);
  g.stroke();
  const tube = (s: string, y: number, maxH: number, col: string, font: (px: number) => string) => {
    const px = fit(g, s, font, W * 0.86, maxH);
    g.font = font(px);
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.lineJoin = "round";
    g.shadowColor = col;
    g.shadowBlur = px * 0.35;
    g.strokeStyle = col;
    g.lineWidth = px * 0.1;
    g.strokeText(s, W / 2, y);
    g.shadowBlur = 0;
    g.strokeStyle = mix(col, "#ffffff", 0.75);
    g.lineWidth = px * 0.035;
    g.strokeText(s, W / 2, y);
  };
  if (b) {
    tube(a, H * 0.4, H * 0.5, colour, BLOCK);
    tube(b, H * 0.78, H * 0.2, "#6ff2d8", BOLD);
  } else tube(a, H * 0.52, H * 0.66, colour, SCRIPT);
}

/** A painted tin sign: cream letters on packing-house green, rivets and a streak of rust. */
function paintTin(g: G, W: number, H: number, text: string) {
  const [a = "", b = ""] = text.split("|");
  g.fillStyle = "#2f5e4a";
  g.fillRect(0, 0, W, H);
  g.strokeStyle = "#f2e6c8";
  g.lineWidth = H * 0.035;
  g.strokeRect(H * 0.07, H * 0.07, W - H * 0.14, H - H * 0.14);
  g.fillStyle = "#f2e6c8";
  for (const [x, y] of [[0.035, 0.05], [0.965, 0.05], [0.035, 0.92], [0.965, 0.92]] as const) {
    g.beginPath();
    g.arc(W * x, H * y + H * 0.02, H * 0.02, 0, Math.PI * 2);
    g.fill();
  }
  g.textAlign = "center";
  g.textBaseline = "middle";
  fit(g, a, BLOCK, W * 0.8, H * 0.36);
  g.fillText(a, W / 2, H * 0.37);
  fit(g, b, BOLD, W * 0.8, H * 0.24);
  g.fillStyle = "#f29b30";
  g.fillText(b, W / 2, H * 0.7);
  g.fillStyle = "rgba(122, 70, 40, 0.55)";
  for (const x of [0.2, 0.71]) g.fillRect(W * x, H * 0.08, W * 0.012, H * 0.5);
}

/** A hand-painted arrow board: yellow, pointing the way, black letters. */
function paintArrow(g: G, W: number, H: number, text: string) {
  const [a = "", b = ""] = text.split("|");
  g.clearRect(0, 0, W, H);
  const tip = W * 0.84;
  g.fillStyle = "#f2cc4e";
  g.beginPath();
  g.moveTo(W * 0.02, H * 0.14);
  g.lineTo(tip, H * 0.14);
  g.lineTo(W * 0.98, H * 0.5);
  g.lineTo(tip, H * 0.86);
  g.lineTo(W * 0.02, H * 0.86);
  g.closePath();
  g.fill();
  g.strokeStyle = INK;
  g.lineWidth = H * 0.05;
  g.stroke();
  g.fillStyle = INK;
  g.textAlign = "center";
  g.textBaseline = "middle";
  fit(g, a, BLOCK, tip * 0.9, H * 0.34);
  g.fillText(a, tip * 0.5, H * 0.4);
  fit(g, b, BOLD, tip * 0.86, H * 0.2);
  g.fillText(b, tip * 0.5, H * 0.68);
}

/** Letters carved in a stone frieze: shadowed, with a lit lower edge. */
function paintCarved(g: G, W: number, H: number, text: string) {
  g.fillStyle = "#f2ead6";
  g.fillRect(0, 0, W, H);
  g.textAlign = "center";
  g.textBaseline = "middle";
  const px = fit(g, text, BLOCK, W * 0.92, H * 0.74);
  g.fillStyle = "rgba(255, 252, 240, 0.9)";
  g.fillText(text, W / 2 + px * 0.04, H / 2 + px * 0.04);
  g.fillStyle = "#7a6c52";
  g.fillText(text, W / 2, H / 2);
}

/** A yellow diamond warning sign with a gator on it, its words on a plate beneath. */
function paintDiamond(g: G, W: number, H: number, text: string) {
  const [a = "", b = ""] = text.split("|");
  g.clearRect(0, 0, W, H);
  const cx = W / 2;
  const cy = H * 0.4;
  const r = H * 0.38;
  g.fillStyle = "#f2cc4e";
  g.beginPath();
  g.moveTo(cx, cy - r);
  g.lineTo(cx + r, cy);
  g.lineTo(cx, cy + r);
  g.lineTo(cx - r, cy);
  g.closePath();
  g.fill();
  g.strokeStyle = INK;
  g.lineWidth = H * 0.03;
  g.stroke();
  // The gator, in profile: snout, back and tail, and four stubby legs.
  g.fillStyle = INK;
  g.beginPath();
  g.moveTo(cx - r * 0.62, cy + r * 0.05);
  g.lineTo(cx - r * 0.25, cy - r * 0.12);
  g.lineTo(cx + r * 0.3, cy - r * 0.1);
  g.lineTo(cx + r * 0.66, cy + r * 0.06);
  g.lineTo(cx + r * 0.3, cy + r * 0.08);
  g.lineTo(cx - r * 0.3, cy + r * 0.12);
  g.closePath();
  g.fill();
  for (const dx of [-0.2, 0.05, 0.15, -0.1]) g.fillRect(cx + dx * r * 1.4, cy + r * 0.08, r * 0.07, r * 0.16);
  g.fillStyle = "#f6f1e1";
  g.fillRect(W * 0.08, H * 0.8, W * 0.84, H * 0.18);
  g.strokeRect(W * 0.08, H * 0.8, W * 0.84, H * 0.18);
  g.fillStyle = INK;
  g.textAlign = "center";
  g.textBaseline = "middle";
  fit(g, a, BLOCK, W * 0.78, H * 0.09);
  g.fillText(a, cx, H * 0.85);
  fit(g, b, BOLD, W * 0.78, H * 0.065);
  g.fillText(b, cx, H * 0.935);
}

/** A green street blade, as Orlando's: white letters, a white border, the fountain's emblem. */
function paintStreet(g: G, W: number, H: number, text: string) {
  const [name = "", sub = ""] = text.split("|");
  g.fillStyle = "#2c6b4c";
  roundRect(g, 0, 0, W, H, H * 0.12);
  g.fill();
  g.strokeStyle = "#ffffff";
  g.lineWidth = H * 0.05;
  roundRect(g, H * 0.07, H * 0.07, W - H * 0.14, H - H * 0.14, H * 0.08);
  g.stroke();
  // The emblem: the fountain's dome and jet.
  const ex = H * 0.62;
  g.fillStyle = "#ffffff";
  g.beginPath();
  g.ellipse(ex, H * 0.64, H * 0.28, H * 0.16, 0, Math.PI, 0);
  g.fill();
  g.beginPath();
  g.moveTo(ex - H * 0.04, H * 0.5);
  g.lineTo(ex, H * 0.2);
  g.lineTo(ex + H * 0.04, H * 0.5);
  g.fill();
  g.textAlign = "left";
  g.textBaseline = "middle";
  const x = H * 1.05;
  fit(g, name, BOLD, W - x - H * 0.9, H * 0.62);
  g.fillText(name, x, H * 0.54);
  if (sub) {
    g.textAlign = "right";
    fit(g, sub, BOLD, H * 0.8, H * 0.3);
    g.fillText(sub, W - H * 0.2, H * 0.36);
  }
}
